import { z } from 'zod'

import { ProjectTypeEnum } from './project'
import { TaskStatusTypeEnum } from './task-status'

// Column mapping rule types
export enum ColumnMappingTypeEnum {
  STATUS_TYPE = 'statusType', // Match by TaskStatusTypeEnum
  STATUS_ID = 'statusId', // Match by exact status ID
  STATUS_NAME = 'statusName', // Match by status name pattern
}

export enum ColumnMatchTypeEnum {
  EXACT = 'exact',
  CONTAINS = 'contains',
  STARTS_WITH = 'startsWith',
  ENDS_WITH = 'endsWith',
}

// Zod schema for column mapping rule
export const ColumnMappingSchema = z.object({
  type: z.nativeEnum(ColumnMappingTypeEnum),
  value: z.string(),
  matchType: z
    .nativeEnum(ColumnMatchTypeEnum)
    .optional()
    .default(ColumnMatchTypeEnum.EXACT),
})

export type ColumnMapping = z.infer<typeof ColumnMappingSchema>

// Zod schema for column configuration
export const BoardColumnConfigSchema = z.object({
  id: z.string(),
  name: z.string(),
  color: z.string().default('#6B7280'),
  order: z.number(),
  mappingRules: z.array(ColumnMappingSchema).default([]),
})

export type BoardColumnConfig = z.infer<typeof BoardColumnConfigSchema>

// Board view mode
export enum BoardViewModeEnum {
  UNIFIED = 'unified', // Single set of columns across all task groups
  GROUPED = 'grouped', // Separate columns per task group (legacy behavior)
}

// Zod schema for project board settings
export const ProjectBoardSettingsSchema = z.object({
  viewMode: z.nativeEnum(BoardViewModeEnum).default(BoardViewModeEnum.UNIFIED),
  columns: z.array(BoardColumnConfigSchema).default([]),
  showUngroupedTasks: z.boolean().default(true),
  defaultColumnId: z.string().optional(),
  // WIP limits per column (optional)
  wipLimits: z.record(z.string(), z.number()).optional(),
})

export type ProjectBoardSettings = z.infer<typeof ProjectBoardSettingsSchema>

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const cloneColumnMapping = (rule: ColumnMapping): ColumnMapping => ({
  ...rule,
  matchType: rule.matchType ?? ColumnMatchTypeEnum.EXACT,
})

const cloneBoardColumn = (column: BoardColumnConfig): BoardColumnConfig => ({
  ...column,
  mappingRules: column.mappingRules.map(cloneColumnMapping),
})

function normalizeBoardColumn(
  column: unknown,
  fallback: BoardColumnConfig,
  index: number,
): BoardColumnConfig {
  if (!isRecord(column)) {
    return { ...cloneBoardColumn(fallback), order: index }
  }

  const mappingRules = Array.isArray(column.mappingRules)
    ? column.mappingRules
        .filter((rule): rule is ColumnMapping => isRecord(rule))
        .map(rule => ({
          type: Object.values(ColumnMappingTypeEnum).includes(
            rule.type as ColumnMappingTypeEnum,
          )
            ? (rule.type as ColumnMappingTypeEnum)
            : ColumnMappingTypeEnum.STATUS_TYPE,
          value:
            typeof rule.value === 'string'
              ? rule.value
              : TaskStatusTypeEnum.NOT_STARTED,
          matchType: Object.values(ColumnMatchTypeEnum).includes(
            rule.matchType as ColumnMatchTypeEnum,
          )
            ? (rule.matchType as ColumnMatchTypeEnum)
            : ColumnMatchTypeEnum.EXACT,
        }))
    : []

  return {
    id: typeof column.id === 'string' && column.id ? column.id : fallback.id,
    name:
      typeof column.name === 'string' && column.name
        ? column.name
        : fallback.name,
    color:
      typeof column.color === 'string' && column.color
        ? column.color
        : fallback.color,
    order: typeof column.order === 'number' ? column.order : index,
    mappingRules,
  }
}

// Helper to create a column mapping rule
export function createStatusTypeMapping(
  statusType: TaskStatusTypeEnum,
): ColumnMapping {
  return {
    type: ColumnMappingTypeEnum.STATUS_TYPE,
    value: statusType,
    matchType: ColumnMatchTypeEnum.EXACT,
  }
}

export function createStatusNameMapping(
  pattern: string,
  matchType: ColumnMatchTypeEnum = ColumnMatchTypeEnum.CONTAINS,
): ColumnMapping {
  return {
    type: ColumnMappingTypeEnum.STATUS_NAME,
    value: pattern.toLowerCase(),
    matchType,
  }
}

export function createStatusIdMapping(statusId: string): ColumnMapping {
  return {
    type: ColumnMappingTypeEnum.STATUS_ID,
    value: statusId,
    matchType: ColumnMatchTypeEnum.EXACT,
  }
}

// Default column configurations by project type
export const DEFAULT_BOARD_COLUMNS: Record<
  ProjectTypeEnum,
  BoardColumnConfig[]
> = {
  [ProjectTypeEnum.BASIC]: [
    {
      id: 'todo',
      name: 'To Do',
      color: '#E5E7EB',
      order: 1,
      mappingRules: [createStatusTypeMapping(TaskStatusTypeEnum.NOT_STARTED)],
    },
    {
      id: 'in-progress',
      name: 'In Progress',
      color: '#60A5FA',
      order: 2,
      mappingRules: [createStatusTypeMapping(TaskStatusTypeEnum.IN_PROGRESS)],
    },
    {
      id: 'done',
      name: 'Done',
      color: '#34D399',
      order: 3,
      mappingRules: [createStatusTypeMapping(TaskStatusTypeEnum.COMPLETED)],
    },
  ],
  [ProjectTypeEnum.KANBAN]: [
    {
      id: 'backlog',
      name: 'Backlog',
      color: '#9CA3AF',
      order: 1,
      mappingRules: [
        createStatusNameMapping('backlog', ColumnMatchTypeEnum.CONTAINS),
      ],
    },
    {
      id: 'todo',
      name: 'To Do',
      color: '#E5E7EB',
      order: 2,
      mappingRules: [createStatusTypeMapping(TaskStatusTypeEnum.NOT_STARTED)],
    },
    {
      id: 'in-progress',
      name: 'In Progress',
      color: '#60A5FA',
      order: 3,
      mappingRules: [createStatusTypeMapping(TaskStatusTypeEnum.IN_PROGRESS)],
    },
    {
      id: 'review',
      name: 'In Review',
      color: '#A78BFA',
      order: 4,
      mappingRules: [
        createStatusNameMapping('review', ColumnMatchTypeEnum.CONTAINS),
      ],
    },
    {
      id: 'done',
      name: 'Done',
      color: '#34D399',
      order: 5,
      mappingRules: [createStatusTypeMapping(TaskStatusTypeEnum.COMPLETED)],
    },
  ],
  [ProjectTypeEnum.SCRUM]: [
    {
      id: 'backlog',
      name: 'Backlog',
      color: '#9CA3AF',
      order: 1,
      mappingRules: [
        createStatusNameMapping('backlog', ColumnMatchTypeEnum.CONTAINS),
      ],
    },
    {
      id: 'ready',
      name: 'Ready',
      color: '#E5E7EB',
      order: 2,
      mappingRules: [
        createStatusNameMapping('ready', ColumnMatchTypeEnum.CONTAINS),
        createStatusTypeMapping(TaskStatusTypeEnum.NOT_STARTED),
      ],
    },
    {
      id: 'in-progress',
      name: 'In Progress',
      color: '#60A5FA',
      order: 3,
      mappingRules: [createStatusTypeMapping(TaskStatusTypeEnum.IN_PROGRESS)],
    },
    {
      id: 'review',
      name: 'In Review',
      color: '#A78BFA',
      order: 4,
      mappingRules: [
        createStatusNameMapping('review', ColumnMatchTypeEnum.CONTAINS),
      ],
    },
    {
      id: 'done',
      name: 'Done',
      color: '#34D399',
      order: 5,
      mappingRules: [createStatusTypeMapping(TaskStatusTypeEnum.COMPLETED)],
    },
  ],
  [ProjectTypeEnum.WATERFALL]: [
    {
      id: 'planning',
      name: 'Planning',
      color: '#E5E7EB',
      order: 1,
      mappingRules: [
        createStatusNameMapping('planning', ColumnMatchTypeEnum.CONTAINS),
        createStatusTypeMapping(TaskStatusTypeEnum.NOT_STARTED),
      ],
    },
    {
      id: 'development',
      name: 'Development',
      color: '#60A5FA',
      order: 2,
      mappingRules: [
        createStatusNameMapping('development', ColumnMatchTypeEnum.CONTAINS),
        createStatusTypeMapping(TaskStatusTypeEnum.IN_PROGRESS),
      ],
    },
    {
      id: 'testing',
      name: 'Testing',
      color: '#A78BFA',
      order: 3,
      mappingRules: [
        createStatusNameMapping('test', ColumnMatchTypeEnum.CONTAINS),
      ],
    },
    {
      id: 'done',
      name: 'Completed',
      color: '#34D399',
      order: 4,
      mappingRules: [createStatusTypeMapping(TaskStatusTypeEnum.COMPLETED)],
    },
  ],
}

// Get default board settings for a project type
export function getDefaultBoardSettings(
  projectType: ProjectTypeEnum,
): ProjectBoardSettings {
  const columns =
    DEFAULT_BOARD_COLUMNS[projectType] ||
    DEFAULT_BOARD_COLUMNS[ProjectTypeEnum.BASIC]
  return {
    viewMode: BoardViewModeEnum.UNIFIED,
    columns: columns.map(cloneBoardColumn),
    showUngroupedTasks: true,
    defaultColumnId: columns[0]?.id,
  }
}

export function normalizeProjectBoardSettings(
  projectType: ProjectTypeEnum,
  settings?: Partial<ProjectBoardSettings> | null,
): ProjectBoardSettings {
  const defaults = getDefaultBoardSettings(projectType)

  if (!isRecord(settings)) {
    return defaults
  }

  const sourceColumns =
    Array.isArray(settings.columns) && settings.columns.length > 0
      ? settings.columns
      : defaults.columns
  // `defaults.columns` is always non-empty (DEFAULT_BOARD_COLUMNS entries
  // each define ≥ 1 column), but `noUncheckedIndexedAccess` widens
  // `defaults.columns[0]` to include `undefined`. Bail out to the defaults
  // if that invariant is ever broken so the fallback below is type-safe.
  const fallbackColumn = defaults.columns[0]
  if (!fallbackColumn) {
    return defaults
  }
  const columns = sourceColumns.map((column, index) =>
    normalizeBoardColumn(
      column,
      defaults.columns[index] ?? fallbackColumn,
      index,
    ),
  )

  const defaultColumnId =
    typeof settings.defaultColumnId === 'string' &&
    columns.some(column => column.id === settings.defaultColumnId)
      ? settings.defaultColumnId
      : columns[0]?.id

  const wipLimits = isRecord(settings.wipLimits)
    ? Object.fromEntries(
        Object.entries(settings.wipLimits).filter(
          ([, value]) => typeof value === 'number',
        ),
      )
    : undefined

  return {
    viewMode: Object.values(BoardViewModeEnum).includes(
      settings.viewMode as BoardViewModeEnum,
    )
      ? (settings.viewMode as BoardViewModeEnum)
      : defaults.viewMode,
    columns,
    showUngroupedTasks:
      typeof settings.showUngroupedTasks === 'boolean'
        ? settings.showUngroupedTasks
        : defaults.showUngroupedTasks,
    defaultColumnId,
    ...(wipLimits ? { wipLimits } : {}),
  }
}

// Status info for matching
interface StatusInfo {
  id: string
  name: string
  statusTypeId: string
}

// Check if a status matches a mapping rule
export function statusMatchesRule(
  status: StatusInfo,
  rule: ColumnMapping,
): boolean {
  switch (rule.type) {
    case ColumnMappingTypeEnum.STATUS_TYPE:
      return status.statusTypeId === rule.value

    case ColumnMappingTypeEnum.STATUS_ID:
      return status.id === rule.value

    case ColumnMappingTypeEnum.STATUS_NAME: {
      const statusName = status.name.toLowerCase()
      const pattern = rule.value.toLowerCase()

      switch (rule.matchType) {
        case ColumnMatchTypeEnum.EXACT:
          return statusName === pattern
        case ColumnMatchTypeEnum.CONTAINS:
          return statusName.includes(pattern)
        case ColumnMatchTypeEnum.STARTS_WITH:
          return statusName.startsWith(pattern)
        case ColumnMatchTypeEnum.ENDS_WITH:
          return statusName.endsWith(pattern)
        default:
          return statusName.includes(pattern)
      }
    }

    default:
      return false
  }
}

// Find which column a status belongs to
export function findColumnForStatus(
  status: StatusInfo,
  columns: BoardColumnConfig[],
  defaultColumnId?: string,
): BoardColumnConfig | undefined {
  // Sort columns by order to ensure consistent priority
  const sortedColumns = [...columns].sort((a, b) => a.order - b.order)

  // First pass: check name-based rules (more specific)
  for (const column of sortedColumns) {
    for (const rule of column.mappingRules) {
      if (
        rule.type === ColumnMappingTypeEnum.STATUS_NAME ||
        rule.type === ColumnMappingTypeEnum.STATUS_ID
      ) {
        if (statusMatchesRule(status, rule)) {
          return column
        }
      }
    }
  }

  // Second pass: check status type rules (less specific, fallback)
  for (const column of sortedColumns) {
    for (const rule of column.mappingRules) {
      if (rule.type === ColumnMappingTypeEnum.STATUS_TYPE) {
        if (statusMatchesRule(status, rule)) {
          return column
        }
      }
    }
  }

  // Fallback to default column
  if (defaultColumnId) {
    return columns.find(s => s.id === defaultColumnId)
  }

  // Ultimate fallback: first column
  return sortedColumns[0]
}

// Group statuses by column
export function groupStatusesByColumn(
  statuses: StatusInfo[],
  columns: BoardColumnConfig[],
  defaultColumnId?: string,
): Map<string, StatusInfo[]> {
  const result = new Map<string, StatusInfo[]>()

  // Initialize all columns with empty arrays
  for (const column of columns) {
    result.set(column.id, [])
  }

  // Group statuses
  for (const status of statuses) {
    const column = findColumnForStatus(status, columns, defaultColumnId)
    if (column) {
      const existing = result.get(column.id) || []
      existing.push(status)
      result.set(column.id, existing)
    }
  }

  return result
}
